import { AuthenticatedMedia } from '@/features/chatbot/components/AuthenticatedMedia';
import type { InboxMessage } from '../api/inboxApi';

function metadataString(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function fileName(message: InboxMessage): string {
  const name = metadataString(message.fileMetadata?.name).slice(0, 256);
  if (name.trim()) {
    return name;
  }
  return message.fileUrl?.split('/').pop()?.split('?')[0] || 'Archivo adjunto';
}

function messageKind(message: InboxMessage): 'audio' | 'image' | 'file' | 'text' {
  if (!message.fileUrl) {
    return 'text';
  }
  const mime = metadataString(message.fileMetadata?.mimeType).toLowerCase();
  if (message.contentType === 'AUDIO' || mime.startsWith('audio/')) {
    return 'audio';
  }
  if (message.contentType === 'IMAGE' || mime.startsWith('image/')) {
    return 'image';
  }
  return 'file';
}

function caption(message: InboxMessage): string | null {
  const text = message.content.slice(0, 4096).trim();
  if (!text) {
    return null;
  }
  const name = fileName(message).trim().toLowerCase();
  if (text.toLowerCase() === name) {
    return null;
  }
  return text;
}

export function InboxAttachment({ message }: { message: InboxMessage }) {
  const kind = messageKind(message);
  const text = caption(message);

  if (kind === 'text') {
    return text ? <div className='whitespace-pre-wrap'>{text}</div> : null;
  }

  if (!message.fileUrl) {
    return text ? <div className='whitespace-pre-wrap'>{text}</div> : null;
  }

  if (kind === 'image') {
    return (
      <AuthenticatedMedia url={message.fileUrl}>
        {src => (
          <div className='space-y-2'>
            <img
              src={src}
              alt={fileName(message)}
              className='max-h-80 rounded-md border object-contain'
              loading='lazy'
            />
            {text ? <div className='whitespace-pre-wrap'>{text}</div> : null}
            <a href={src} target='_blank' rel='noopener noreferrer' className='block text-xs underline'>
              Ver original
            </a>
          </div>
        )}
      </AuthenticatedMedia>
    );
  }

  if (kind === 'audio') {
    return (
      <AuthenticatedMedia url={message.fileUrl}>
        {src => (
          <div className='space-y-2'>
            <audio controls className='w-full' src={src}>
              <a href={src}>Descargar audio</a>
            </audio>
            {text ? <div className='whitespace-pre-wrap'>{text}</div> : null}
          </div>
        )}
      </AuthenticatedMedia>
    );
  }

  return (
    <AuthenticatedMedia url={message.fileUrl}>
      {src => (
        <div className='space-y-2'>
          <a href={src} target='_blank' rel='noopener noreferrer' className='block text-sm font-medium underline'>
            {fileName(message)}
          </a>
          {text ? <div className='whitespace-pre-wrap'>{text}</div> : null}
        </div>
      )}
    </AuthenticatedMedia>
  );
}
